/**
 * Date filter utility functions for converting preset filters to ISO date strings.
 * All dates are calculated in UTC timezone.
 */

export type DateFilterPreset =
	| 'last_30_days'
	| 'this_month'
	| 'last_month'
	| 'this_quarter'
	| 'last_quarter';

export interface DateRange {
	since: string; // ISO date string (required)
	before?: string; // ISO date string (optional, only for ranges like "last_month")
}

/**
 * Converts a preset date filter to ISO date strings.
 *
 * @param filter - The preset filter to convert
 * @returns Object with `since` (required) and optionally `before` (optional) ISO date strings
 */
export function getDateRangeForFilter(filter: DateFilterPreset): DateRange {
	const now = new Date();
	const utcNow = new Date(
		Date.UTC(
			now.getUTCFullYear(),
			now.getUTCMonth(),
			now.getUTCDate(),
			now.getUTCHours(),
			now.getUTCMinutes(),
			now.getUTCSeconds(),
			now.getUTCMilliseconds(),
		),
	);

	switch (filter) {
		case 'last_30_days': {
			// 30 days ago from now (since only, no before)
			const since = new Date(utcNow);
			since.setUTCDate(since.getUTCDate() - 30);
			since.setUTCHours(0, 0, 0, 0);
			return {
				since: since.toISOString(),
			};
		}

		case 'this_month': {
			// First day of current month at 00:00:00 UTC (since only)
			const since = new Date(
				Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 1, 0, 0, 0, 0),
			);
			return {
				since: since.toISOString(),
			};
		}

		case 'last_month': {
			// First day and last day of previous month (since and before)
			const lastMonth = new Date(
				Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1),
			);
			const since = new Date(
				Date.UTC(
					lastMonth.getUTCFullYear(),
					lastMonth.getUTCMonth(),
					1,
					0,
					0,
					0,
					0,
				),
			);
			const before = new Date(
				Date.UTC(
					lastMonth.getUTCFullYear(),
					lastMonth.getUTCMonth() + 1,
					0,
					23,
					59,
					59,
					999,
				),
			);
			return {
				since: since.toISOString(),
				before: before.toISOString(),
			};
		}

		case 'this_quarter': {
			// First day of current quarter at 00:00:00 UTC (since only)
			const currentQuarter = Math.floor(utcNow.getUTCMonth() / 3);
			const since = new Date(
				Date.UTC(utcNow.getUTCFullYear(), currentQuarter * 3, 1, 0, 0, 0, 0),
			);
			return {
				since: since.toISOString(),
			};
		}

		case 'last_quarter': {
			// First day and last day of previous quarter (since and before)
			const currentQuarter = Math.floor(utcNow.getUTCMonth() / 3);
			const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
			const lastQuarterYear =
				currentQuarter === 0
					? utcNow.getUTCFullYear() - 1
					: utcNow.getUTCFullYear();
			const since = new Date(
				Date.UTC(lastQuarterYear, lastQuarter * 3, 1, 0, 0, 0, 0),
			);
			const before = new Date(
				Date.UTC(lastQuarterYear, (lastQuarter + 1) * 3, 0, 23, 59, 59, 999),
			);
			return {
				since: since.toISOString(),
				before: before.toISOString(),
			};
		}

		default:
			// Fallback to last_30_days
			const fallbackSince = new Date(utcNow);
			fallbackSince.setUTCDate(fallbackSince.getUTCDate() - 30);
			fallbackSince.setUTCHours(0, 0, 0, 0);
			return {
				since: fallbackSince.toISOString(),
			};
	}
}

/**
 * Gets a human-readable label for a date filter preset.
 *
 * @param filter - The preset filter
 * @returns Human-readable label
 */
export function getDateFilterLabel(filter: DateFilterPreset): string {
	const labels: Record<DateFilterPreset, string> = {
		last_30_days: 'Last 30 Days',
		this_month: 'This Month',
		last_month: 'Last Month',
		this_quarter: 'This Quarter',
		last_quarter: 'Last Quarter',
	};
	return labels[filter] || filter;
}
